import {
  UseMutateAsyncFunction,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  CompanyPeopleListResponse,
  CustomerCompanyAPI,
} from 'api/customer-company';
import {
  CreateCustomerCompanyPayload,
  CustomerCompany,
  UpdateCustomerCompanyPayload,
} from 'api/customer-company/types';
import { Widget } from 'api/dashboard';
import { WidgetSourceType } from 'api/dashboard/widget';
import { FileGroupSourceType } from 'api/files';
import { User } from 'api/models';
import { NoteSourceType } from 'api/note';
import { Project, ProjectAPI } from 'api/project';
import { StatusUpdate } from 'api/status-updates';
import { ViewApI } from 'api/views';
import { View, ViewType } from 'api/views/types';
import { ApiCustomerCompanyListViewDefinition } from 'components/organisms/customer-portfolio/company-list/saved-views/types';
import { CustomerCompanyListFilter } from 'components/organisms/customer-portfolio/company-list/url-params/types';
import { OrgNodeApi } from 'components/organisms/customer-portfolio/org-chart/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import { useFileGroupsQuery } from 'queries/files';
import {
  AllNotesBySourceKey,
  GetAllNotesQueryOption,
  useGetAllCompanyNotes,
} from 'queries/note';
import { useRefetchProjectDetail } from 'queries/project';
import {
  QueryWidgetsBySourceKey,
  useGetAllWidgetsBySource,
} from 'queries/widgets';
import { added, updated } from 'stores/companies';

type CustomerCompanyQueryKeyType = ['companies', 'customer', number];
export const getCustomerCompanyQueryKey = (
  companyId: number,
): CustomerCompanyQueryKeyType => ['companies', 'customer', companyId];

const getCustomerCompanyQueryKeyWithTimeRangeFinancials = (
  companyId: number,
  filter: CustomerCompanyListFilter,
) => ['companies', 'customer', companyId, 'financials', filter];

export const getPeopleHierarchyQueryKey = (companyId: number) => [
  'people',
  'hierarchy',
  companyId,
];

export const getPeopleListQueryKey = (companyId: number) => [
  'people',
  'list',
  companyId,
];

export const useGetCustomerCompanyById = (
  companyId: number,
  options: UseQueryOptions<CustomerCompany, unknown, CustomerCompany> = {},
): UseQueryResult<CustomerCompany> =>
  useQuery<CustomerCompany, unknown, CustomerCompany>(
    getCustomerCompanyQueryKey(companyId),
    () => CustomerCompanyAPI.getCompanyById(companyId),
    {
      refetchOnMount: true,
      staleTime: GENERAL_API_STALE,
      ...options,
    },
  );

export const useGetCustomerCompanyByIdWithTimeRangeFinancials = (
  companyId: number,
  filter: CustomerCompanyListFilter,
  options: UseQueryOptions<CustomerCompany, unknown, CustomerCompany> = {},
): UseQueryResult<CustomerCompany> =>
  useQuery<CustomerCompany, unknown, CustomerCompany>(
    getCustomerCompanyQueryKeyWithTimeRangeFinancials(companyId, filter),
    () => CustomerCompanyAPI.getCompanyById(companyId, filter),
    {
      refetchOnMount: true,
      staleTime: GENERAL_API_STALE,
      ...options,
    },
  );
export const useRefetchCustomerCompanyById = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (companyId: number) =>
      queryClient.invalidateQueries(getCustomerCompanyQueryKey(companyId)),
    [queryClient],
  );
};

export const useGetWidgetsForCompanies = (
  options: UseQueryOptions<
    Widget[],
    unknown,
    Widget[],
    QueryWidgetsBySourceKey
  > = {},
) => useGetAllWidgetsBySource(WidgetSourceType.Company, options);

/**
 * Get valid reportees for a user (users who can report to this manager).
 * Returns full User objects directly from backend.
 */
export const useGetManagerOfOptionsQuery = (
  userId: number,
  options: UseQueryOptions<User[], unknown, User[]> = {},
): UseQueryResult<User[]> =>
  useQuery<User[], unknown, User[]>(
    ['orgChart', 'managerOf', userId],
    () => CustomerCompanyAPI.getValidManagerOfOptions(userId),
    options,
  );

/**
 * Get valid managers for a user (users this person can report to).
 * Returns full User objects directly from backend.
 */
export const useGetReportsToOptionsQuery = (
  userId: number,
  options: UseQueryOptions<User[], unknown, User[]> = {},
): UseQueryResult<User[]> =>
  useQuery<User[], unknown, User[]>(
    ['orgChart', 'reportsTo', userId],
    () => CustomerCompanyAPI.getValidReportingToOptions(userId),
    options,
  );

export const useGetPeopleHierarchyQuery = (
  companyId: number,
  options: UseQueryOptions<OrgNodeApi[], unknown, OrgNodeApi[]> = {},
): UseQueryResult<OrgNodeApi[]> =>
  useQuery<OrgNodeApi[], unknown, OrgNodeApi[]>(
    ['people', 'hierarchy', companyId],
    () => CustomerCompanyAPI.getPeopleHierarchy(companyId),
    options,
  );

export const useGetAllNotesByCompanyId = (
  companyId: number,
  options: GetAllNotesQueryOption = {},
) =>
  useGetAllCompanyNotes(
    {
      sourceId: companyId,
      sourceType: NoteSourceType.Company,
      subSourceTypes: [NoteSourceType.Project],
    },
    options,
  );

export const useRefetchAllNotesByCompanyId = () => {
  const queryClient = useQueryClient();
  const refetchCustomerCompany = useRefetchCustomerCompanyById();

  return useCallback(
    async (companyId: number) => {
      await queryClient.invalidateQueries(
        AllNotesBySourceKey({
          sourceId: companyId,
          sourceType: NoteSourceType.Company,
        }),
      );
      await refetchCustomerCompany(companyId);
    },
    [queryClient, refetchCustomerCompany],
  );
};

export const useRefetchAllNotes = () => {
  const queryClient = useQueryClient();
  const refetchCustomerCompany = useRefetchCustomerCompanyById();
  const refetchProjectDetail = useRefetchProjectDetail();

  return useCallback(
    async (sourceId: number, sourceType: NoteSourceType) => {
      await queryClient.invalidateQueries(
        AllNotesBySourceKey({
          sourceId,
          sourceType,
        }),
      );
      if (sourceType === NoteSourceType.Company) {
        await refetchCustomerCompany(sourceId);
      } else {
        await refetchProjectDetail(sourceId);
      }
    },
    [queryClient, refetchCustomerCompany, refetchProjectDetail],
  );
};

export const useGetAllFileGroupsByCompanyId = (companyId: number) =>
  useFileGroupsQuery({
    sourceId: companyId,
    sourceType: FileGroupSourceType.Company,
  });

export const useRefetchAllFileGroupsByCompanyId = (companyId: number) => {
  const { refetch } = useFileGroupsQuery({
    sourceId: companyId,
    sourceType: FileGroupSourceType.Company,
  });

  return useCallback(async () => {
    await refetch();
  }, [refetch]);
};

export const useCompanyPeopleListQuery = (
  companyId: number,
  options: UseQueryOptions<
    CompanyPeopleListResponse,
    unknown,
    CompanyPeopleListResponse
  > = {},
): UseQueryResult<CompanyPeopleListResponse> => {
  return useQuery<
    CompanyPeopleListResponse,
    unknown,
    CompanyPeopleListResponse
  >(
    getPeopleListQueryKey(companyId),
    () => CustomerCompanyAPI.getCompanyPeopleList(companyId),
    options,
  );
};
export const getStatusUpdatesByCompanyKey = (companyId: number) => [
  'status-updates',
  'by-company-id',
  companyId,
];
export const useGetStatusUpdatesByCompany = (
  companyId: number,
  options: UseQueryOptions<StatusUpdate[], unknown, StatusUpdate[]> = {},
): UseQueryResult<StatusUpdate[], unknown> =>
  useQuery<StatusUpdate[], unknown, StatusUpdate[]>(
    getStatusUpdatesByCompanyKey(companyId),
    () => CustomerCompanyAPI.getStatusUpdatesByCompanyId(companyId),
    options,
  );
export const useRefetchStatusUpdateByCompany = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (companyId: number) => {
      queryClient.invalidateQueries(getStatusUpdatesByCompanyKey(companyId));
    },
    [queryClient],
  );
};
export const CustomerCompanyListViews = ['customer-company', 'views'];

export const useGetCustomerCompanyListViewsQuery = (
  queryOptions: UseQueryOptions<
    View<ApiCustomerCompanyListViewDefinition>[],
    unknown,
    View<ApiCustomerCompanyListViewDefinition>[],
    typeof CustomerCompanyListViews
  > = {},
) =>
  useQuery(
    CustomerCompanyListViews,
    () =>
      ViewApI.getAllViews<ApiCustomerCompanyListViewDefinition>(
        ViewType.CustomerPorfolioList,
      ),
    queryOptions,
  );
type ProjectsByCompanyIdKeyType = [string, number];
export const getProjectsByCompanyIdKey = (
  companyId: number,
): ProjectsByCompanyIdKeyType => ['projects-by-customer', companyId];

export const useGetProjectsByCompany = (
  companyId: number,
  options: UseQueryOptions<Project[], unknown, Project[]> = {},
) =>
  useQuery<Project[], unknown, Project[]>(
    getProjectsByCompanyIdKey(companyId),
    () => ProjectAPI.getProjectsByCustomer(companyId),
    options,
  );

export const useCreateCompanyMutation = (
  options: UseMutationOptions<
    CustomerCompany,
    unknown,
    CreateCustomerCompanyPayload
  > = {},
) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<CustomerCompany, unknown, CreateCustomerCompanyPayload>(
    (payload) => CustomerCompanyAPI.createCompany(payload),
    {
      onSuccess: async (company) => {
        dispatch(added(company));
        await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
      },
      ...options,
    },
  );
};

export type UpdateCustomerCompanyMutationFunctionType = UseMutateAsyncFunction<
  CustomerCompany,
  unknown,
  UpdateCustomerCompanyVariable
>;
type UpdateCustomerCompanyVariable = {
  companyId: number;
  payload: UpdateCustomerCompanyPayload;
};
export const useUpdateCustomerCompanyMutation = (
  options: UseMutationOptions<
    CustomerCompany,
    unknown,
    UpdateCustomerCompanyVariable
  > = {},
) => {
  const dispatch = useDispatch();
  const refetchCustomerCompany = useRefetchCustomerCompanyById();

  return useMutation<CustomerCompany, unknown, UpdateCustomerCompanyVariable>(
    ({ companyId, payload }) =>
      CustomerCompanyAPI.updateCompany(companyId, payload),
    {
      onSuccess: async (company) => {
        dispatch(updated(company));
        await refetchCustomerCompany(company.companyId);
      },
      ...options,
    },
  );
};

export const useDeleteCustomerCompanyMutation = (
  onSuccess?: () => void,
): UseMutationResult<void, unknown, number> => {
  return useMutation<void, unknown, number>(
    (accountId) => CustomerCompanyAPI.deleteCustomerCompany(accountId),
    {
      onSuccess: async () => {
        if (onSuccess) {
          onSuccess();
        }
      },
    },
  );
};

type MergeCompanyPayload = {
  destinationCompanyId: number;
  sourceCompanyIds: number[];
};

export const useMergeCompanyMutation = (
  options: UseMutationOptions<unknown, unknown, MergeCompanyPayload> = {},
) => {
  return useMutation<unknown, unknown, MergeCompanyPayload>(
    ({ destinationCompanyId, sourceCompanyIds }) =>
      CustomerCompanyAPI.mergeCompanies(sourceCompanyIds, destinationCompanyId),
    options,
  );
};

export const useGetAllCustomerCompanies = (
  options: UseQueryOptions<CustomerCompany[], unknown, CustomerCompany[]> = {},
): UseQueryResult<CustomerCompany[]> => {
  return useQuery<CustomerCompany[], unknown, CustomerCompany[]>(
    ['customer-companies'],
    () => CustomerCompanyAPI.getAllCustomerCompanies(),
    options,
  );
};

export const useGetCustomerCompanyExport = () => {
  return useMutation<any, unknown, { columnOrder: any }, any>((data) =>
    CustomerCompanyAPI.exportCustomerCompanies(data),
  );
};
